import { AND, NOT, OR, hasValueScope, inferScopeType } from '@/lib/filter/scope-tree';
import { GOAL_PLANNING, GOAL_RELATION } from '@/lib/props/custom-types';
import { dateFn, directDateFn } from '@/lib/filter/base-frontend-filter/date';
import { directNumberFn, numberFn } from '@/lib/filter/base-frontend-filter/number';
import { directOptionsFn, optionsFn } from '@/lib/filter/base-frontend-filter/options';
import { directPlanningsFn } from '@/lib/filter/base-frontend-filter/plannings';
import { directRelationFn } from '@/lib/filter/base-frontend-filter/relations';
import { directSpaceFn, spaceFn } from '@/lib/filter/base-frontend-filter/space';
import { directTextFn, textFn } from '@/lib/filter/base-frontend-filter/text';
import { directUserFn, staticUserFn, userFn } from '@/lib/filter/base-frontend-filter/user';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { lookupFn } from '@/lib/filter/base-frontend-filter/lookup';
import { propertyType, userScopeType } from 'shared/constants.json';
import { reverseFn } from '@/lib/filter/base-frontend-filter/reverse';

const translateStaticScope = ({ entity, scope }) => {
  if (!isNullOrUndefined(scope.staticUsers)) {
    return staticUserFn({ entity, scope });
  }
  return false;
};

const translateIndirectPropertyScope = ({ entity, scope }) => {
  const indirectEntity = entity[scope.indirectProperty.edgeName];

  if (Array.isArray(indirectEntity)) {
    return indirectEntity.some((e) => translateScope({ entity: e, scope: scope.indirectProperty.scope, propertyValues: e[scope.indirectProperty.scope.edgeName] }));
  }

  return translateScope({ entity: indirectEntity, scope: scope.indirectProperty.scope, propertyValues: indirectEntity[scope.indirectProperty.scope.edgeName] });
};

const translateDirectPropertyScope = ({ entity, scope }) => {
  switch (scope.directProperty.type) {
    case propertyType.text:
      return directTextFn({ entity, scope });
    case propertyType.number:
      return directNumberFn({ entity, scope });
    case propertyType.date:
      return directDateFn({ entity, scope });
    case propertyType.user:
      return directUserFn({ entity, scope });
    case propertyType.space:
      return directSpaceFn({ entity, scope });
    case propertyType.options:
    case propertyType.singleSelect:
      return directOptionsFn({ entity, scope });
    case GOAL_PLANNING:
      return directPlanningsFn({ entity, scope });
    case GOAL_RELATION:
      return directRelationFn({ entity, scope });
    default:
      return false;
  }
};

const translatePropertyScope = ({ entity, scope, propertyValues, lookupPropertySvc }) => {
  if (isNullOrUndefined(propertyValues)) {
    return false;
  }
  switch (scope.property.type) {
    case propertyType.text:
      return textFn({ propertyValues, scope });
    case propertyType.number:
      return numberFn({ propertyValues, scope });
    case propertyType.date:
      return dateFn({ propertyValues, scope });
    case propertyType.user:
      return userFn({ propertyValues, scope });
    case propertyType.space:
      return spaceFn({ propertyValues, scope });
    case propertyType.options:
    case propertyType.status:
    case propertyType.singleSelect:
      return optionsFn({ propertyValues, scope });
    case propertyType.lookup:
      return lookupFn({ entity, scope, propertyValues, lookupPropertySvc });
    default:
      return false;
  }
};

const translateScope = ({ entity, scope, propertyValues, lookupPropertySvc }) => {
  switch (inferScopeType(scope)) {
    case userScopeType.staticUsers:
      return translateStaticScope({ entity, scope });
    case userScopeType.indirectProperty:
      return translateIndirectPropertyScope({ entity, scope });
    case userScopeType.directProperty:
      return translateDirectPropertyScope({ entity, scope });
    case userScopeType.property:
      return translatePropertyScope({ entity, scope, propertyValues, lookupPropertySvc });
    case userScopeType.reverseProperty:
      return reverseFn({ entity, scope });
    default:
      throw new Error('user scope type not supported');
  }
};

const translateTree = ({ parentOp = OR, isFilterMode = false }) => ({ entity, tree, propertyValues, lookupPropertySvc }) => {
  let op = tree.op;
  if (isNullOrUndefined(tree.op) || tree.op === '') {
    op = OR;
  }

  if ((isNullOrUndefined(tree.children) || tree.children.length === 0)) {
    if (!hasValueScope(tree.scope)) {
      return isFilterMode === (parentOp !== NOT);
    }

    return translateScope({ entity, scope: tree.scope, propertyValues, lookupPropertySvc });
  }

  for (let i = 0; i < tree.children.length; i++) {
    const valid = translateTree({ parentOp: op, isFilterMode })({ entity, tree: tree.children[i], propertyValues, lookupPropertySvc });
    if (op === NOT) {
      return !valid;
    }
    if (op === OR && valid) {
      return true;
    }
    if (op === AND && !valid) {
      return false;
    }
  }
  return op === AND;
};

// isFilterMode means skip evaluation on empty branches, i.e., branches where the scope on the leaf is invalid or empty
export const isInFilter = ({ isFilterMode = false } = { isFilterMode: false }) => ({ entity, scopeTree = null, propertyValues = undefined, lookupPropertySvc }) => {
  if (scopeTree === null) {
    return true;
  }

  if (isNullOrUndefined(entity)) {
    return false;
  }

  const pvs = propertyValues === undefined ? entity.properties : propertyValues;
  return translateTree({ isFilterMode })({ entity, tree: scopeTree, propertyValues: pvs, lookupPropertySvc });
};
